/**
 * Pubblicazione delle app mobili sugli store (ADR 0045), rotte di `tools` sotto
 * `/api/v1/tools/projects/{id}/mobile`.
 *
 * Le credenziali degli store si scrivono e si cancellano; non si leggono mai: le risposte
 * dicono solo quali ci sono. Provvisorio dichiarato, come gli altri client: percorsi e tipi
 * scritti a mano, presidiati da `mobileRelease.test.ts`.
 */

import type { ApiClient } from './client';

/** Tipi di credenziale, nell'ordine dell'interfaccia. */
export const CREDENTIAL_KINDS = [
  'android_upload_key',
  'android_play_service_account',
  'ios_asc_api_key',
  'ios_signing',
] as const;

/** Tipo di credenziale. */
export type CredentialKind = (typeof CREDENTIAL_KINDS)[number];

/** Stato di una pubblicazione. */
export type ReleaseState =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'credentials_issued'
  | 'expired'
  | 'succeeded'
  | 'failed';

/** Identificativi delle app negli store. */
export interface MobileSettings {
  /** Pacchetto Android (es. `it.esempio.app`). */
  android_package: string;
  /** Bundle id iOS. */
  ios_bundle_id: string;
  /** Team id Apple. */
  ios_team_id: string;
  /** Chi li ha cambiati per ultimo. */
  updated_by: string;
  /** Ultima modifica, ISO 8601; `null` se mai scritti. */
  updated_at: string | null;
}

/** Una credenziale presente (mai il valore). */
export interface MobileCredential {
  /** Tipo. */
  kind: CredentialKind;
  /** Chi l'ha caricata. */
  updated_by: string;
  /** Quando, ISO 8601. */
  updated_at: string;
}

/** Una pubblicazione proposta da un agente. */
export interface MobileRelease {
  /** Identificativo. */
  id: string;
  /** Store. */
  platform: 'android' | 'ios';
  /** Repository GitHub del progetto. */
  repository: string;
  /** Commit da pubblicare. */
  commit_sha: string;
  /** Canale dello store. */
  track: string;
  /** Versione leggibile. */
  version_label: string;
  /** Note dell'agente: testo semplice, da mostrare come dato. */
  notes: string;
  /** Stato. */
  state: ReleaseState;
  /** Proprietario del set che ha proposto. */
  proposed_by: string;
  /** Set da cui arriva la proposta. */
  proposed_from_set: string;
  /** Chi ha deciso. */
  decided_by: string;
  /** Quando, ISO 8601. */
  decided_at: string | null;
  /** Fine della validità dell'approvazione, ISO 8601. */
  approval_expires_at: string | null;
  /** Esecuzione di GitHub Actions. */
  run_url: string;
  /** Dettaglio dell'ultimo passo. */
  detail: string;
  /** Proposta, ISO 8601. */
  created_at: string;
}

/** Tutto il blocco della scheda «Strumenti». */
export interface MobileOverview {
  /** Identificativi delle app. */
  settings: MobileSettings;
  /** Credenziali presenti. */
  credentials: MobileCredential[];
  /** Ultime pubblicazioni. */
  releases: MobileRelease[];
  /** Repository dei workflow riutilizzabili. */
  publisher_repo: string;
  /** False se l'ultimo avvio del workflow è stato rifiutato per permessi. */
  actions_dispatch: boolean;
}

/** Esito della sincronizzazione del repository dell'editore. */
export interface PublisherSync {
  /** `owner/nome`. */
  repository: string;
  /** True se il repository è stato creato adesso. */
  created: boolean;
  /** Percorso → True se scritto. */
  files: Record<string, boolean>;
  /** Workflow attesi ma assenti dal pacchetto di `tools`. */
  missing: string[];
  /** Livello di accesso dei workflow. */
  access_level: string;
}

/** Campi modificabili degli identificativi. */
export type MobileSettingsInput = Pick<MobileSettings, 'android_package' | 'ios_bundle_id' | 'ios_team_id'>;

/**
 * Prefisso delle rotte di un progetto.
 *
 * @param projectId Progetto.
 * @returns Il percorso.
 */
/** Stato di una build dell'APK (ADR 0062). */
export type AppBuildState = 'queued' | 'dispatched' | 'signing' | 'succeeded' | 'failed' | 'expired';

/** Una build dell'APK di un progetto (ADR 0062). */
export interface AppBuild {
  /** Identificativo. */
  id: string;
  /** Oggi solo `android`. */
  platform: string;
  /** Stato. */
  state: AppBuildState;
  /** Versione leggibile letta dall'APK; vuota finché la build non riesce. */
  version_name: string;
  /** `versionCode` letto dall'APK. */
  version_code: number | null;
  /** Pacchetto Android. */
  package: string;
  /** Dimensione dell'APK in byte. */
  size_bytes: number | null;
  /** SHA-256 dell'APK. */
  sha256: string;
  /** Motivo di un errore o ultime righe del log. */
  detail: string;
  /** True se l'ha chiesta l'Institore. */
  from_institore: boolean;
  /** Istante della richiesta. */
  created_at: string | null;
  /** Fine della build. */
  finished_at: string | null;
  /**
   * Variante (ADR 0062 §10): `demo` parla con la demo, `production` con la produzione;
   * vuota per le build di prima delle varianti.
   */
  variant?: AppVariant | '';
  /** Sito con cui parla l'APK; vuoto senza variante. */
  site_url?: string;
  /** Scadenza dell'accesso alla demo dentro l'APK di demo; `null` per la produzione. */
  demo_access_expires_at?: string | null;
}

/** Variante di un APK (ADR 0062 §10). */
export type AppVariant = 'demo' | 'production';

/** Build di un progetto. */
export interface AppBuildList {
  /** Ultime build, le più recenti prima. */
  builds: AppBuild[];
  /** Build riuscite tenute per progetto. */
  keep: number;
}

/** URL firmato di un APK. */
export interface AppBuildDownload {
  /** Indirizzo, valido pochi minuti. */
  url: string;
  /** Scadenza. */
  expires_at: string;
  /** Nome del file. */
  file_name: string;
}

/** Certificato che firma gli APK di un progetto (ADR 0062 §7): dati pubblici. */
export interface AppSigning {
  /** False se il progetto non ha ancora una chiave: nasce alla prima build. */
  present: boolean;
  /** Impronta SHA-1 nella forma di keytool (`AB:CD:...`). */
  sha1: string;
  /** Impronta SHA-256, nella stessa forma. */
  sha256: string;
  /** Soggetto del certificato. */
  subject: string;
  /** Fine della validità. */
  valid_until: string | null;
}

function base(projectId: string): string {
  return `/api/v1/tools/projects/${encodeURIComponent(projectId)}/mobile`;
}

function builds(projectId: string): string {
  return `/api/v1/tools/projects/${encodeURIComponent(projectId)}/app-builds`;
}

/** Operazioni della pubblicazione mobile, esposte alla UI. */
export class MobileReleaseApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Identificativi, credenziali presenti e pubblicazioni.
   *
   * @param projectId Progetto.
   * @returns Il blocco.
   */
  overview(projectId: string): Promise<MobileOverview> {
    return this.client.request<MobileOverview>('GET', base(projectId));
  }

  /**
   * Scrive gli identificativi delle app.
   *
   * @param projectId Progetto.
   * @param settings Campi.
   * @returns Gli identificativi salvati.
   */
  saveSettings(projectId: string, settings: MobileSettingsInput): Promise<MobileSettings> {
    return this.client.request<MobileSettings>('PUT', `${base(projectId)}/settings`, settings);
  }

  /**
   * Carica o sostituisce una credenziale.
   *
   * @param projectId Progetto.
   * @param kind Tipo.
   * @param value Campi del tipo (ADR 0045 §3).
   * @returns Tipo, autore e istante; mai il valore.
   */
  putCredential(projectId: string, kind: CredentialKind, value: Record<string, unknown>): Promise<MobileCredential> {
    return this.client.request<MobileCredential>('PUT', `${base(projectId)}/credentials/${kind}`, value);
  }

  /**
   * Cancella una credenziale.
   *
   * @param projectId Progetto.
   * @param kind Tipo.
   */
  deleteCredential(projectId: string, kind: CredentialKind): Promise<void> {
    return this.client.request<void>('DELETE', `${base(projectId)}/credentials/${kind}`);
  }

  /**
   * Approva una pubblicazione: `tools` crea il tag e avvia il workflow.
   *
   * @param projectId Progetto.
   * @param releaseId Pubblicazione.
   * @returns La riga aggiornata.
   */
  approve(projectId: string, releaseId: string): Promise<MobileRelease> {
    return this.client.request<MobileRelease>(
      'POST',
      `${base(projectId)}/releases/${encodeURIComponent(releaseId)}/approve`,
    );
  }

  /**
   * Crea o aggiorna il repository dell'editore con i workflow riutilizzabili (solo `ADMIN`).
   *
   * @returns L'esito.
   */
  syncPublisher(): Promise<PublisherSync> {
    return this.client.request<PublisherSync>('POST', '/api/v1/tools/mobile/publisher/sync');
  }

  /**
   * Rifiuta una pubblicazione.
   *
   * @param projectId Progetto.
   * @param releaseId Pubblicazione.
   * @returns La riga aggiornata.
   */
  reject(projectId: string, releaseId: string): Promise<MobileRelease> {
    return this.client.request<MobileRelease>(
      'POST',
      `${base(projectId)}/releases/${encodeURIComponent(releaseId)}/reject`,
    );
  }

  /**
   * Ultime build dell'APK di un progetto (ADR 0062).
   *
   * @param projectId Progetto.
   * @returns Le build.
   */
  appBuilds(projectId: string): Promise<AppBuildList> {
    return this.client.request<AppBuildList>('GET', builds(projectId));
  }

  /**
   * Avvia una build dell'APK della variante scelta (ADR 0062 §10). Passa da `agents`, che
   * aggiunge il sito dell'ambiente e, per la demo, l'accesso alla demo privata.
   *
   * @param setId Set con il progetto in scrittura.
   * @param projectId Progetto.
   * @param variant `demo` o `production`.
   * @returns La build avviata.
   * @throws ApiError 409 `demo_not_ready`, `production_not_ready`, `app_build_running`, ...
   */
  startAppBuild(setId: string, projectId: string, variant: AppVariant): Promise<AppBuild> {
    return this.client.request<AppBuild>(
      'POST',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/app-builds`,
      { variant },
    );
  }

  /**
   * URL firmato per scaricare l'APK di una build riuscita.
   *
   * @param projectId Progetto.
   * @param buildId Build.
   * @returns L'indirizzo, valido pochi minuti.
   */
  appBuildDownload(projectId: string, buildId: string): Promise<AppBuildDownload> {
    return this.client.request<AppBuildDownload>(
      'GET',
      `${builds(projectId)}/${encodeURIComponent(buildId)}/download`,
    );
  }

  /**
   * Impronte del certificato di firma degli APK, per registrare l'app presso Google.
   *
   * @param projectId Progetto (serve `write`: la chiave si legge dall'archivio dei segreti).
   * @returns Il certificato; `present` falso se il progetto non ha ancora una chiave.
   */
  appSigning(projectId: string): Promise<AppSigning> {
    return this.client.request<AppSigning>('GET', `/api/v1/tools/projects/${encodeURIComponent(projectId)}/app-signing`);
  }
}
